import type { ResearchDepth } from '@ni/shared';

/**
 * **The research section's words** — moved here from the office dashboard's
 * `lib/copy.ts` on 2026-09-04, with the controls they belong to.
 *
 * The depth per status was the office's for a day, with the spend limit
 * already carved out of it; then the whole bag became the operator's, because what it names is the cost and shape
 * of the pipeline rather than anything about the people an org is asking about.
 * The copy travelled with the controls rather than being rewritten: the reader
 * changed, but what these settings *do* did not, and a second wording of the
 * same three sentences would only be a second thing to keep true.
 *
 * The words stay the plain ones. Nothing here says extractor, facet, round or
 * token — even for an operator, "how much work a run does" is the question being
 * answered, and the terms that make it answerable are pages and areas. `depth`
 * is the one word with no plain synonym, so the control is named for the
 * question instead and the three levels are described by what they read.
 *
 * This app's other copy is inline in the screen that says it — `routes/org/`,
 * `routes/orgs/` — which is right for a screen's worth of labels. These are here
 * because there are enough of them to read as a block, and because reading them
 * beside each other is how their tone stays one tone.
 */

export const RESEARCH_DEPTH_LABELS: Record<ResearchDepth, string> = {
  full: 'Full',
  standard: 'Standard',
  light: 'Light',
};

/**
 * **The org page's tabs** (2026-09-10), in the order an operator needs them.
 *
 * Members is first and is the default, because it is what the page is opened
 * for nine times in ten: somebody has joined, somebody has left. The settings
 * tabs follow in the order the questions come in — what a run may do, what the
 * month costs, what the account holds, how the workspace connects. There is no
 * Import tab: an import has no settings of its own (run defaults sweep,
 * 2026-09-26).
 *
 * The words are short and are not sentences: a tab is a place, not a
 * description of one. Connector is spelled without "Claude" because the section
 * inside it says that, and a tab strip is not where a product name earns its
 * room.
 */
export const ORG_TAB_LABELS = {
  members: 'Members',
  research: 'Research',
  spend: 'Spend',
  credits: 'Credits',
  connector: 'Connector',
} as const;

export const RESEARCH_SECTION_NOTE =
  'How much work a research run does, and what it may spend doing it. Applies to new runs; ' +
  'runs already underway keep the settings they started with.';

export const RESEARCH_DEPTH_LABEL = 'How much research each person gets';

export const RESEARCH_DEPTH_HELP =
  'Full reads every area and goes back for a second round. Standard reads every area once. ' +
  'Light reads background, network and affiliations in a single pass.';

/** The first option of every research control: the organization has chosen nothing. */
export const RESEARCH_DEFAULT_OPTION = 'Use the default';

/**
 * The line under a control nobody has set — what a run would use today.
 *
 * Shown **only when the control is unset**, because that is the only time the
 * question "and if I leave this alone?" is open. Printed under a control that
 * has been set, "Default: Opus" reads as a description of the value showing,
 * which is the misreading it exists to prevent.
 *
 * The value comes from `@ni/shared` (`RESEARCH_DEPTH_DEFAULTS_BY_VERTICAL` and
 * the two constants beside it), which is where the server reads it too — a panel
 * naming its own default would show one no run would use.
 */
export function defaultNote(value: string): string {
  return `Default: ${value}`;
}

/**
 * **The label for one status in an organization's own vocabulary**, derived
 * rather than declared.
 *
 * The dashboard keeps a per-vertical label table because it renders those words
 * everywhere and in sentences; this app renders them in one place, as the label
 * of one control each. A second copy of every vertical's vocabulary here would
 * be a table that silently goes stale — a vertical that adds a status would show
 * a blank label on the surface whose job is to serve every vertical there is —
 * so the status key is tidied instead. The statuses themselves come from
 * `SUBJECT_VOCABULARY_BY_VERTICAL`, so the *set* is never guessed at; only its
 * capitalisation is.
 *
 * It agrees with the dashboard's `SUBJECT_STATUS_LABELS` today by
 * **capitalisation, not by construction**: every status this build carries is a
 * single lowercase word whose label is that word capitalised. A vertical whose
 * label is not its key tidied — a multi-word status, or one the office reads
 * under a different noun — would diverge here, and the fix then is to move the
 * label table into `@ni/shared` and have both surfaces read it.
 */
export function statusLabel(status: string): string {
  const words = status.replace(/[_-]+/g, ' ').trim();
  if (words.length === 0) return status;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/* ---- the Claude connector panel (2026-09-07) ---- */

export const CONNECTOR_PANEL_TITLE = 'Claude connector';

/**
 * **Three labels and three values, and nothing else** (owner ruling,
 * 2026-09-10).
 *
 * There used to be a paragraph here saying what an owner does with the strings
 * and which Claude plan a workspace has to be on, and a Show / Hide button in
 * front of them. Both are gone. The paragraph was written for a reader who did
 * not know what the connector was; the only person who reaches this tab is the
 * operator who is already on the phone explaining it, and a screen that explains
 * things to somebody who is doing the explaining is a screen they read past. The
 * reveal was cheap secrecy — the values arrive over the same authenticated read
 * either way — and it cost a click on every visit.
 *
 * So the tab is the three labels below, filled in as soon as it opens.
 */
export const CONNECTOR_URL_LABEL = 'Connector URL';
export const CONNECTOR_CLIENT_ID_LABEL = 'Client ID';
export const CONNECTOR_SECRET_LABEL = 'Client secret';

/** Not configured on this deployment — different from an empty string. */
export const CONNECTOR_UNSET = 'Not configured';
